import { useContext, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ReactComponent as CALENDAR } from "../../../assets/CalendarICON.svg"
import { ReactComponent as HOUSE } from "../../../assets/HouseICON.svg"
import { ReactComponent as TASKS } from "../../../assets/Task.svg"
import BaseButton from "../../../components/elements/BaseButton"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import BaseModal from "../../../components/layout/BaseModal"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { GeneralQueryKeys } from "../../../api/constants"
import { getObjectsForTourStops, getTourTasks } from "../api/apiCalls"
import { isActionableStatus, weekLabel } from "../data/tourTasks"
import { TourObjectRef, TourStop, TourWeekday, TOUR_WEEKDAYS } from "../types"

type PlanStopRef = {
    objectId: string
    weekday: TourWeekday
}

type StopKind = "plan" | "openTask" | "newTask"

type AddStopModalType = {
    modalId: number
    defaultWeekday: TourWeekday
    planStops: PlanStopRef[]
    onSave: (data: { object: TourObjectRef; weekday: TourWeekday; adHoc?: TourStop["adHocTask"] }) => void
}

const KIND_OPTIONS: { value: StopKind; title: string; text: string }[] = [
    { value: "plan", title: "Fahrplan", text: "Wiederkehrender Stopp an diesem Wochentag." },
    { value: "openTask", title: "Offene Aufgabe", text: "Bestehende Objektaufgabe diesem Einsatz zuordnen." },
    { value: "newTask", title: "Einzelaufgabe", text: "Neue außerplanmäßige Aufgabe anlegen und einplanen." },
]

export default function AddStopModal(props: AddStopModalType) {
    const modalCon = useContext(ModalContext)
    const { data: objects, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.OBJECTS_ADDRESS_ONLY + "tour"],
        queryFn: getObjectsForTourStops,
    })
    const [objectId, setObjectId] = useState("")
    const [weekday, setWeekday] = useState<TourWeekday>(props.defaultWeekday)
    const [kind, setKind] = useState<StopKind>("plan")
    const [taskId, setTaskId] = useState("")
    const [adHocText, setAdHocText] = useState("")

    const { data: objectTasks, isLoading: tasksLoading } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_TASKS + objectId],
        queryFn: () => getTourTasks({ objectId }),
        enabled: !!objectId && kind === "openTask",
    })

    const occupiedOnDay = useMemo(
        () => new Set(props.planStops.filter((item) => item.weekday === weekday).map((item) => item.objectId)),
        [props.planStops, weekday],
    )
    const available = useMemo(
        () => (objects ?? []).filter((item) => kind !== "plan" || !occupiedOnDay.has(item._id)),
        [objects, occupiedOnDay, kind],
    )
    const daysForSelectedObject = useMemo(() => {
        if (!objectId) return []
        const values = props.planStops.filter((item) => item.objectId === objectId).map((item) => item.weekday)
        return TOUR_WEEKDAYS.filter((day) => values.includes(day.value))
    }, [objectId, props.planStops])
    const openTasks = useMemo(
        () =>
            (objectTasks ?? [])
                .filter((task) => isActionableStatus(task.status))
                .map((task) => ({
                    _id: task._id,
                    name: `${task.activity} · ${weekLabel(task)}`,
                    activity: task.activity,
                })),
        [objectTasks],
    )
    const selectedTask = openTasks.find((task) => task._id === taskId)

    const reset = () => {
        setObjectId("")
        setWeekday(props.defaultWeekday)
        setKind("plan")
        setTaskId("")
        setAdHocText("")
    }

    const canSave =
        !!objectId && (kind === "plan" || (kind === "openTask" && !!taskId) || (kind === "newTask" && !!adHocText.trim()))

    const addStop = () => {
        const object = (objects ?? []).find((item) => item._id === objectId)
        if (!object || !canSave) return
        props.onSave({
            object,
            weekday,
            adHoc:
                kind === "plan"
                    ? undefined
                    : kind === "openTask"
                    ? { _id: taskId, activity: selectedTask?.activity ?? "", kind: "openTask", taskId }
                    : { _id: `adhoc-${Date.now()}`, activity: adHocText.trim(), kind: "newTask" },
        })
        reset()
        modalCon?.trigger(props.modalId)
    }

    return (
        <BaseModal
            modalId={props.modalId}
            size="fit"
            title="Objekt zur Tour"
            onOpen={() => {
                reset()
                setWeekday(props.defaultWeekday)
            }}
            bottomRow={
                <div className="flex justify-end w-full">
                    <BaseButton label="Hinzufügen" func={addStop} colorway="secondary" disabled={!canSave} />
                </div>
            }>
            <p className="text-sm text-gray-500 px-2 pt-4 pb-3">
                Planstopp für den wöchentlichen Fahrplan oder außerplanmäßiger Einsatz mit bestehender oder neuer Aufgabe.
            </p>
            <div className="flex flex-col gap-5 px-2 pb-2 min-w-[22rem] max-w-[32rem]">
                <section className="flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 pl-2">Objekt</p>
                    <div className="flex gap-3 items-start">
                        <HOUSE className="w-6 h-6 shrink-0 mt-8 text-primary" />
                        <BaseInputSelection
                            label="Objekt *"
                            placeholder="Auswählen"
                            data={available}
                            value={objectId || undefined}
                            setId={(id) => {
                                setObjectId(id)
                                setTaskId("")
                                if (kind === "plan" && id && props.planStops.some((item) => item.objectId === id && item.weekday === weekday)) {
                                    const next = TOUR_WEEKDAYS.find((day) => !props.planStops.some((item) => item.objectId === id && item.weekday === day.value))
                                    if (next) setWeekday(next.value)
                                }
                            }}
                            searchable={false}
                            customLoading={isLoading}
                        />
                    </div>
                    {daysForSelectedObject.length > 0 && kind === "plan" && (
                        <p className="text-xs text-gray-500 pl-11">Bereits im Fahrplan: {daysForSelectedObject.map((day) => day.label).join(", ")}</p>
                    )}
                </section>
                <section className="flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 pl-2">Wochentag</p>
                    <div className="flex gap-3 items-start">
                        <CALENDAR className="w-6 h-6 shrink-0 mt-1 text-primary" />
                        <div className="flex flex-col w-full">
                            <label className="text-sm pl-2 pb-1">Tag *</label>
                            <div className="flex flex-wrap gap-2">
                                {TOUR_WEEKDAYS.map((day) => {
                                    const occupied = kind === "plan" && objectId ? props.planStops.some((item) => item.objectId === objectId && item.weekday === day.value) : false
                                    return (
                                        <button
                                            key={day.value}
                                            type="button"
                                            disabled={occupied}
                                            title={occupied ? "Objekt ist an diesem Tag bereits zugeordnet" : day.label}
                                            className={`btn btn-sm ${weekday === day.value ? "btn-primary" : "btn-ghost bg-gray-100"} disabled:opacity-40`}
                                            onClick={() => {
                                                if (!occupied) setWeekday(day.value)
                                            }}>
                                            {day.short}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </section>
                <section className="flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 pl-2">Einsatz</p>
                    <div className="flex gap-3 items-start">
                        <div className="flex flex-col gap-2 w-full">
                            {KIND_OPTIONS.map((option) => {
                                const selected = kind === option.value
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`text-left rounded-default px-3 py-2 border ${selected ? "border-primary bg-primary/5" : "border-transparent bg-gray-50 hover:bg-gray-100"}`}
                                        onClick={() => {
                                            setKind(option.value)
                                            setTaskId("")
                                            setAdHocText("")
                                        }}>
                                        <p className="text-sm font-semibold">{option.title}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{option.text}</p>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </section>
                {kind === "openTask" && (
                    <section className="flex flex-col gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 pl-2">Aufgabe</p>
                        <div className="flex gap-3 items-start">
                            <TASKS className="w-6 h-6 shrink-0 mt-8 text-primary" />
                            <BaseInputSelection
                                label="Offene Aufgabe *"
                                placeholder={objectId ? "Aufgabe wählen" : "Zuerst Objekt wählen"}
                                data={openTasks}
                                value={taskId || undefined}
                                setId={setTaskId}
                                searchable={false}
                                customLoading={tasksLoading}
                                disabled={!objectId}
                            />
                        </div>
                        {objectId && !tasksLoading && openTasks.length === 0 && (
                            <p className="text-xs text-gray-500 pl-11">Keine offenen Aufgaben an diesem Objekt.</p>
                        )}
                    </section>
                )}
                {kind === "newTask" && (
                    <section className="flex flex-col gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 pl-2">Aufgabe</p>
                        <div className="flex gap-3 items-start">
                            <TASKS className="w-6 h-6 shrink-0 mt-8 text-primary" />
                            <BaseInputField
                                label="Einzelaufgabe *"
                                type="text"
                                placeholder="z. B. Sonderreinigung Eingang"
                                onChange={setAdHocText}
                            />
                        </div>
                    </section>
                )}
            </div>
        </BaseModal>
    )
}
